import { createFileRoute, Outlet, useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { fetchEnrollmentByIdQueryOptions } from "@/api/enrollments";
import { PageHeader } from "@/components/PageHeader";
import { useApiParameters } from "@/hooks/useApiParameters";
import { EnrollmentDetailsNavigation } from "@/routes/_auth/app/_app/org/$orgId/enrollments/-components/EnrollmentDetailsNavigation";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/enrollments/$enrollmentId",
)({
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const { orgId, enrollmentId } = params;

		// Preload enrollment data
		context.queryClient.ensureQueryData(
			fetchEnrollmentByIdQueryOptions(userId, token, orgId, enrollmentId),
		);

		return { userId, token, orgId, enrollmentId };
	},
	component: EnrollmentDetailsLayout,
});

function EnrollmentDetailsLayout() {
	const { t } = useTranslation();
	const { userId, token } = Route.useLoaderData();
	const location = useLocation();

	// Determine active tab based on current route
	const getActiveTab = (): "edit" | "billing" => {
		const pathname = location.pathname;
		if (pathname.includes("/billing")) return "billing";
		if (pathname.endsWith("/edit")) return "edit";
		return "edit"; // default to edit for now
	};

	return (
		<div className="flex flex-col h-full">
			<PageHeader
				title={t("enrollments.navigation.title")}
				description={t("enrollments.navigation.description")}
				showBackButton={true}
				backButtonText={t("common.back")}
				hideOnMobile={false}
			/>

			{/* Main content area with side navigation */}
			<div
				className="flex-1 flex flex-col md:flex-row h-[calc(100vh-4rem)] min-h-0 overflow-hidden"
				data-testid="enrollment-details-container"
			>
				{/* Side Navigation */}
				<EnrollmentDetailsNavigation
					activeTab={getActiveTab()}
					userId={userId}
					token={token}
				/>

				{/* Main Content Area */}
				<div
					className="flex-1 p-3 md:p-4 bg-base-200 min-h-0 overflow-auto"
					data-testid="enrollment-details-main-content-area"
				>
					<Outlet />
				</div>
			</div>
		</div>
	);
}
